import React, { useMemo } from 'react';
import { useFormik } from 'formik';
import { Button, Grid, CircularProgress, Switch, FormControlLabel } from '@mui/material';
import { Input } from '@fullscreendigital/carrefour-dex-fe';
import { Modal } from 'components/core-ui/Modal';
import { useNotification } from 'hooks';
import { useCreateJobCategoryMutation, useJobCategoryQuery, useUpdateJobCategoryMutation } from '../../api';
import s from 'components/core-ui/Modal/Modal.modules.scss';
import { JobCategory, EditJobCategoryModalProps } from '../../interfaces';
import { jobCategoryValidationSchema } from '../helpers';

const EditJobCategoryModal: React.FC<EditJobCategoryModalProps> = ({ isModalOpen = false, onModalClose, jobCatId, onSuccess }) => {
    const { showNotification } = useNotification();

    const shouldRefreshData = useMemo(() => isModalOpen === true, [isModalOpen]);

    const { data: jobCategory, isLoading } = useJobCategoryQuery(showNotification, jobCatId?.toString(), shouldRefreshData);

    const updateJobCategoryMutation = useUpdateJobCategoryMutation(showNotification);
    const createJobCategoryMutation = useCreateJobCategoryMutation(showNotification);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            name: jobCategory?.name,
            code: jobCategory?.code,
            description: jobCategory?.description,
            active: jobCatId ? jobCategory?.active : true,
        },
        validationSchema: jobCategoryValidationSchema,
        onSubmit: async (values) => {
            const updatedValues: Partial<JobCategory> = values;
            if (!jobCatId) {
                await createJobCategoryMutation.mutateAsync(updatedValues);
            } else {
                updatedValues.id = jobCatId;
                await updateJobCategoryMutation.mutateAsync({ jobCatId, updatedData: updatedValues });
            }
            onSuccess();
            onModalClose();
            formik.resetForm();
        },
    });

    return (
        <Modal isModalOpen={isModalOpen} closeModal={onModalClose} modalTitle={jobCatId ? "Edit Job Category" : "Create Job Category"} modalWidth="50%">
            <Grid container spacing={2}>
                {isLoading || updateJobCategoryMutation.isLoading ? (
                    <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
                ) : (
                    <>
                        <Grid item xs={12}>
                            <Input
                                type="text"
                                variant="outlined"
                                name="name"
                                label="Job category name"
                                width="100%"
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.name && Boolean(formik.errors.name)}
                                helperText={formik.touched.name && formik.errors.name}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Input
                                type="text"
                                variant="outlined"
                                name="code"
                                label="Code"
                                width="100%"
                                value={formik.values.code}
                                disabled={!!jobCatId}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.code && Boolean(formik.errors.code)}
                                helperText={formik.touched.code && formik.errors.code}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Input
                                type="text"
                                variant="outlined"
                                name="description"
                                label="Description"
                                width="100%"
                                value={formik.values.description}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.description && Boolean(formik.errors.description)}
                                helperText={formik.touched.description && formik.errors.description}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel control={
                                <Switch
                                    name="active"
                                    checked={formik.values.active}
                                    onChange={(event) => formik.setFieldValue('active', event.target.checked)}
                                    onBlur={formik.handleBlur}

                                />}
                                label="Active"
                            />
                        </Grid>
                        <Grid item xs={12} className={s.modalButtons}>
                            <Button variant="outlined" onClick={onModalClose}>
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => formik.handleSubmit()}
                                disabled={!formik.isValid || formik.isSubmitting}
                            >
                                Save
                            </Button>
                        </Grid>
                    </>
                )}
            </Grid>
        </Modal>
    );
};

export default EditJobCategoryModal;
